import React, { useEffect, useMemo, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Stack,
  Chip,
  Alert,
  Box,
  CircularProgress,
  Typography,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import { Security, VerifiedUser, Lock, Timer } from '@mui/icons-material';
import axios from 'axios';
import { useSession } from '../../Services/Auth/sessionAuth';
import { useNavigate } from 'react-router-dom';
import { Crypter } from '../../Functions/Security/Crypter';
import { getMethodLabel, mfaApi, MFA_METHOD, normalizeStatusCode, parseEncryptedResponse } from './mfaApi';

interface MfaChallengeDialogProps {
  open: boolean;
  onClose: () => void;
  onMfaSuccess?: (user: any) => void;
  user: {
    coduti: string;
    codage: string;
    nomuti?: string;
    agence?: string;
    codgroup?: string;
    group?: string;
    datjour?: string;
    CaisseUser?: string;
  };
  credentials: {
    codage: string;
    coduti: string;
    motpas: string;
  };
  initialPayload?: any;
}

export default function MfaChallengeDialog({
  open,
  onClose,
  onMfaSuccess,
  user,
  credentials,
  initialPayload,
}: MfaChallengeDialogProps) {
  const { refreshSession } = useSession();
  const navigate = useNavigate();
  const [otp, setOtp] = useState('');
  const methodType = Number(initialPayload?.Method ?? initialPayload?.method ?? MFA_METHOD.emailOtp);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [lockout, setLockout] = useState<{ lockoutSeconds: number; lockoutUntil: string; consecutiveFailures: number } | null>(null);
  const [remainingSeconds, setRemainingSeconds] = useState(0);

  const formatCountdown = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  };

  useEffect(() => {
    if (!lockout || remainingSeconds <= 0) return;
    const interval = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(interval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [lockout, remainingSeconds]);

  useEffect(() => {
    if (remainingSeconds === 0 && lockout) {
      setLockout(null);
      setError('');
    }
  }, [remainingSeconds, lockout]);

  const tempToken = useMemo(() => {
    const value = initialPayload?.TempToken ?? initialPayload?.tempToken ?? initialPayload?.TemporaryToken;
    return typeof value === 'string' ? value : '';
  }, [initialPayload]);

  const handleVerify = async () => {
    if (lockout) return;
    if (!otp.trim() || !tempToken) {
      setError('Veuillez saisir le code OTP et vérifier la session MFA.');
      return;
    }

    setPending(true);
    setError('');
    setSuccess('');

    try {
      const payload = {
        CodUti: credentials.coduti,
        OtpCode: otp,
        MethodType: Number(methodType),
        TemporaryToken: tempToken,
      };

      const config = {
        method: 'post',
        maxBodyLength: Infinity,
        url: mfaApi.verifyOtp,
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
        },
        data: Crypter.encrypt(JSON.stringify(payload)),
      };

      const response = await axios(config);
      const parsed = parseEncryptedResponse(response);
      const status = normalizeStatusCode(parsed);

      if (status === 429 || parsed?.Result?.LockoutSeconds !== undefined) {
        const lockoutSeconds = parsed?.Result?.LockoutSeconds ?? 60;
        const lockoutUntil = parsed?.Result?.LockoutUntil ?? '';
        const consecutiveFailures = parsed?.Result?.ConsecutiveFailures ?? 0;
        setLockout({ lockoutSeconds, lockoutUntil, consecutiveFailures });
        setRemainingSeconds(lockoutSeconds);
        return;
      }
     


      if (status >= 200 && status < 300) {
        const result = parsed?.Result ?? parsed?.result ?? parsed ?? {};
        const role = String(result.codgroup ?? result.CodGroup ?? result.group ?? result.Group ?? result.Role ?? '').trim();

        
        if (await refreshSession()) {
          setSuccess('MFA validé avec succès. Connexion réussie.');
          const mfaResult = {
            coduti: result.coduti ?? result.CodUti ?? credentials.coduti,
            codage: result.codage ?? result.Codage ?? user.codage,
            codgroup: result.codgroup ?? result.CodGroup ?? result.group ?? result.Group ?? result.Role ?? '',
            nomuti: result.nomuti ?? result.NomUti ?? user.nomuti,
            datjour: result.datjour ?? result.DatJour ?? user.datjour,
            CaisseUser: result.CaisseUser ?? user.CaisseUser,
          };
          setTimeout(() => {
            onClose();
            if (onMfaSuccess) {
              onMfaSuccess(mfaResult);
            } else {
              navigate('/');
            }
          }, 700);
          return;
        }
        setError('La session serveur n’a pas pu être initialisée.');
        return;
      }

      const message = parsed?.Message ?? 'Le code MFA est invalide ou expiré.';
      setError(message);
      if (status === 429 || parsed?.Result?.LockoutSeconds !== undefined) {
        const lockoutSeconds = parsed?.Result?.LockoutSeconds ?? 60;
        const lockoutUntil = parsed?.Result?.LockoutUntil ?? '';
        const consecutiveFailures = parsed?.Result?.ConsecutiveFailures ?? 0;
        setLockout({ lockoutSeconds, lockoutUntil, consecutiveFailures });
        setRemainingSeconds(lockoutSeconds);
        return;
      }
      if (status === 401 || message.toLowerCase().includes('expir')) {
        setTimeout(() => {
          onClose();
          navigate('/Login');
        }, 1500);
      }
    } catch (e: any) {

      const parsedError = parseEncryptedResponse(e?.response);
      const message = parsedError?.Message ?? 'Erreur lors de la vérification MFA.';
      setError(message);
      const isLockout = e?.response?.status === 429 || parsedError?.Result?.LockoutSeconds !== undefined;
      if (isLockout) {
        const lockoutSeconds = parsedError?.Result?.LockoutSeconds ?? 60;
        const lockoutUntil = parsedError?.Result?.LockoutUntil ?? '';
        const consecutiveFailures = parsedError?.Result?.ConsecutiveFailures ?? 0;
        setLockout({ lockoutSeconds, lockoutUntil, consecutiveFailures });
        setRemainingSeconds(lockoutSeconds);
      } else if (e?.response?.status === 401 || message.toLowerCase().includes('expir')) {
        setTimeout(() => {
          onClose();
          navigate('/Login');
        }, 1500);
      }
    } finally {
      setPending(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
      <DialogTitle sx={{ 
        color: '#0a6a55', 
        fontWeight: 700, 
        pb: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <Security sx={{ fontSize: 28, color: '#0f9b7e' }} />
        Vérification MFA
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          {/* Information card */}
          <Card sx={{ 
            backgroundColor: '#f0f9f6', 
            border: '1px solid #dbeae3',
            borderRadius: 2
          }}>
            <CardContent sx={{ py: 2 }}>
              <Stack direction="row" alignItems="center" spacing={2}>
                <VerifiedUser sx={{ fontSize: 32, color: '#0f9b7e' }} />
                <Box>
                  <Typography variant="subtitle2" sx={{ color: '#0a6a55', fontWeight: 600 }}>
                    Utilisateur: {user.coduti}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {user.nomuti || 'Utilisateur'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>

          {/* Method indicator */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Chip 
              label={getMethodLabel(methodType)} 
              color="success" 
              variant="outlined"
              sx={{ 
                px: 2, 
                py: 1, 
                fontSize: '1rem',
                fontWeight: 600,
                borderRadius: 2
              }}
              icon={<Lock />}
            />
          </Box>

          {error && <Alert severity="error" onClose={() => setError('')}>{error}</Alert>}
          {success && <Alert severity="success" onClose={() => setSuccess('')}>{success}</Alert>}

          {/* OTP Input */}
          {lockout ? (
            <Alert
              severity="warning"
              sx={{
                backgroundColor: '#fff3e0',
                color: '#e65100',
                border: '1px solid #ffe0b2',
                borderRadius: 2,
                py: 3,
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Trop de tentatives de vérification
              </Typography>
              <Typography variant="body1" sx={{ mt: 1 }}>
                Veuillez patienter avant de pouvoir réessayer.
              </Typography>
              <Box sx={{ mt: 2, textAlign: 'center' }}>
                <Typography variant="h2" sx={{ fontWeight: 700, color: '#c62828', letterSpacing: '0.1em' }}>
                  {formatCountdown(remainingSeconds)}
                </Typography>
              </Box>
            </Alert>
          ) : (
            <TextField
              fullWidth
              label="Code de vérification"
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\s+/g, ''))}
              placeholder="Entrez le code à 6 chiffres"
              inputProps={{
                maxLength: 8,
                inputMode: 'numeric',
                style: {
                  fontSize: '1.5rem',
                  letterSpacing: '0.5rem',
                  textAlign: 'center',
                  fontWeight: 600
                }
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Timer color="action" />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  py: 1.5,
                }
              }}
              autoFocus
            />
          )}

          {/* Info alert */}
          <Alert 
            severity="info" 
            sx={{ 
              backgroundColor: '#e8f5e9',
              color: '#1b5e20',
              border: '1px solid #c8e6c9'
            }}
          >
            <Typography variant="body2">
              Un code temporaire a été envoyé par {getMethodLabel(methodType).toLowerCase()}. 
              Ce code expire dans 5 minutes.
            </Typography>
          </Alert>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
        <Button 
          onClick={() => onClose()} 
          color="inherit"
          sx={{ borderRadius: 2 }}
        >
          Annuler
        </Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleVerify}
          disabled={Boolean(pending || !otp.trim() || !tempToken || lockout)}
          startIcon={pending ? <CircularProgress size={20} color="inherit" /> : <VerifiedUser />}
          sx={{ 
            borderRadius: 2,
            px: 3,
            py: 1.2,
            fontWeight: 600
          }}
        >
          {pending ? 'Vérification...' : 'Vérifier le code'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
